import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReactivationInsights } from '../hooks/useReactivationInsights';
import {
  XMarkIcon,
  ChartBarIcon,
  ClockIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

export default function ReactivationModal() {
  const navigate = useNavigate();
  const insights = useReactivationInsights();
  const [activeTab, setActiveTab] = useState<'new' | 'updated'>('new');
  const [dismissed, setDismissed] = useState(() => {
    // Check sessionStorage for dismissed state
    return sessionStorage.getItem('reactivationModalDismissed') === 'true';
  });

  if (!insights.hasLapsedAccess || insights.loading || dismissed) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('reactivationModalDismissed', 'true');
  };

  const handleReactivate = () => {
    navigate('/upgrade');
  };

  const formatDate = (date: Date | null) => {
    if (!date) return 'Unknown';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getReasonText = (reason: string | null) => {
    const reasons: Record<string, string> = {
      'trial_expired': 'Free trial expired',
      'subscription_cancelled': 'Subscription cancelled',
      'beta_expired': 'Beta access expired',
      'grant_expired': 'Access code expired',
      'grant_revoked': 'Access revoked'
    };
    return reasons[reason || ''] || 'Access expired';
  };

  const hasChanges = insights.totalNewFlows > 0 || insights.totalUpdatedFlows > 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm" />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
            aria-label="Dismiss modal"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>

          {/* Content */}
          <div className="overflow-y-auto max-h-[90vh]">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6 text-white">
              <div className="flex items-start">
                <ChartBarIcon className="h-12 w-12 mr-4 flex-shrink-0" />
                <div>
                  <h2 className="text-2xl font-bold mb-2">
                    Your Salesforce Documentation is Out of Date
                  </h2>
                  <p className="text-blue-100">
                    Access expired {insights.daysLapsed} days ago ({getReasonText(insights.accessLostReason)})
                  </p>
                </div>
              </div>
            </div>

            {/* Connection Issues Alert */}
            {insights.hasConnectionIssues && (
              <div className="mx-8 mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-yellow-900 mb-1">
                      Salesforce Connection Lost
                    </h3>
                    <p className="text-sm text-yellow-800 mb-3">
                      {insights.connectionIssueOrgs.length === 1
                        ? `Connection lost for ${insights.connectionIssueOrgs[0].orgName}. `
                        : `Connection lost for ${insights.connectionIssueOrgs.length} orgs. `}
                      Reconnect within 30 days or data will be deleted.
                    </p>
                    <button
                      onClick={() => navigate('/orgs')}
                      className="inline-flex items-center px-3 py-1.5 bg-yellow-600 text-white text-sm font-medium rounded hover:bg-yellow-700"
                    >
                      <ArrowPathIcon className="h-4 w-4 mr-1.5" />
                      Reconnect Salesforce
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Changes Section */}
            {hasChanges ? (
              <div className="px-8 py-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Changes Detected in Your Salesforce Org
                </h3>

                {/* Tabs */}
                <div className="border-b border-gray-200 mb-4">
                  <nav className="-mb-px flex space-x-8">
                    <button
                      onClick={() => setActiveTab('new')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'new'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      New Flows ({insights.totalNewFlows})
                    </button>
                    <button
                      onClick={() => setActiveTab('updated')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'updated'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Updated Flows ({insights.totalUpdatedFlows})
                    </button>
                  </nav>
                </div>

                {/* Flow Lists */}
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                  {activeTab === 'new' && (
                    <div className="divide-y divide-gray-200">
                      {insights.allNewFlows.length === 0 ? (
                        <div className="p-4 text-center text-gray-500">
                          No new flows detected
                        </div>
                      ) : (
                        insights.allNewFlows.slice(0, 20).map((flow, idx) => (
                          <div key={idx} className="p-3 hover:bg-gray-50">
                            <div className="font-medium text-gray-900">{flow.name}</div>
                            <div className="text-sm text-gray-500">
                              {flow.type}
                              {flow.orgName && Object.keys(insights.byOrg).length > 1 && (
                                <span className="ml-2">• {flow.orgName}</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              Last modified: {formatDate(new Date(flow.lastModified))}
                            </div>
                          </div>
                        ))
                      )}
                      {insights.allNewFlows.length > 20 && (
                        <div className="p-3 text-center text-sm text-gray-500 bg-gray-50">
                          ... and {insights.allNewFlows.length - 20} more
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'updated' && (
                    <div className="divide-y divide-gray-200">
                      {insights.allUpdatedFlows.length === 0 ? (
                        <div className="p-4 text-center text-gray-500">
                          No updated flows detected
                        </div>
                      ) : (
                        insights.allUpdatedFlows.slice(0, 20).map((flow, idx) => (
                          <div key={idx} className="p-3 hover:bg-gray-50">
                            <div className="font-medium text-gray-900">{flow.name}</div>
                            <div className="text-sm text-gray-500">
                              {flow.type}
                              {flow.oldVersion && flow.newVersion && (
                                <span className="ml-2">
                                  • v{flow.oldVersion} → v{flow.newVersion}
                                </span>
                              )}
                              {flow.orgName && Object.keys(insights.byOrg).length > 1 && (
                                <span className="ml-2">• {flow.orgName}</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              Last modified: {formatDate(new Date(flow.lastModified))}
                            </div>
                          </div>
                        ))
                      )}
                      {insights.allUpdatedFlows.length > 20 && (
                        <div className="p-3 text-center text-sm text-gray-500 bg-gray-50">
                          ... and {insights.allUpdatedFlows.length - 20} more
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="px-8 py-6">
                <div className="text-center text-gray-500 py-8">
                  <ChartBarIcon className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                  <p>No changes detected yet.</p>
                  <p className="text-sm mt-1">We're still checking for new flows in your org.</p>
                </div>
              </div>
            )}

            {/* ROI Section */}
            {hasChanges && (
              <div className="px-8 py-6 bg-gray-50 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Impact on Your Team
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center text-gray-600 mb-2">
                      <ClockIcon className="h-5 w-5 mr-2" />
                      <span className="text-sm font-medium">Manual Documentation</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      ~{insights.estimatedHours} hours
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center text-gray-600 mb-2">
                      <CurrencyDollarIcon className="h-5 w-5 mr-2" />
                      <span className="text-sm font-medium">Cost at Your Rate</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      ${insights.estimatedCost.toFixed(2)}
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-lg border border-blue-200 border-2">
                    <div className="flex items-center text-blue-600 mb-2">
                      <ChartBarIcon className="h-5 w-5 mr-2" />
                      <span className="text-sm font-medium">FlowDocs Subscription</span>
                    </div>
                    <div className="text-2xl font-bold text-blue-600">
                      ${insights.monthlySubscriptionPrice}/mo
                    </div>
                  </div>
                </div>

                {insights.savings > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-900 font-semibold">
                      You save ${insights.savings.toFixed(2)} by letting FlowDocs handle this automatically
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Locked Dashboard Message */}
            <div className="px-8 py-6 border-t border-gray-200">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-900">
                  <strong>Your dashboard is waiting for you below.</strong> All your data is safe and will be available when you reactivate. Get instant access to flow issues, error tracking, performance analytics, and automated documentation.
                </p>
              </div>
            </div>

            {/* CTA Section */}
            <div className="px-8 py-6 bg-gray-50 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <button
                  onClick={handleReactivate}
                  className="w-full sm:w-auto px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                >
                  Reactivate FlowDocs - ${insights.monthlySubscriptionPrice}/month
                </button>
                <button
                  onClick={() => navigate('/upgrade')}
                  className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                >
                  View pricing options →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
